import { Injectable, NgZone, signal, Signal } from '@angular/core';
import { localStorageService } from './localstorage.service';
import { ApiService } from './api.service';
import { Device } from '@twilio/voice-sdk';
import { SharedService, Status } from './shared.service';
import { TwilioService } from './twilio.service';
import { Subject } from 'rxjs';
import { buildCommunicationRef } from './communication-ref';

@Injectable({
  providedIn: 'root'
})
export class DialerService {
  activeCall: any;
  isInCall: boolean = false;
  callStartTime?: number;
  call_sid?: string;
  isCallConnected: boolean = false;
  public incomingCall: any;
  isMuted: boolean = false;
  patientName: string = '';
  // Shown in brackets after patientName on the call modal. Used by the business
  // Communication Logs tab to name the business user being dialled; empty for the
  // business's own numbers and for the debt flow.
  subName: string = '';
  split_req_id: any;
  // Set instead of split_req_id when dialing from the business Communication Logs tab.
  // Use setCallContext() rather than assigning either directly -- this service is a
  // singleton, so a stale id from a previous call would otherwise be logged too.
  doctor_id: any;
  /** Set instead of `split_req_id` when the call was placed from contract details. */
  contract_id: any;
  callingStatus: any;
  total_duration: any;
  callDuration: any;
  callDisconnected: boolean = false;
  isReconnecting: boolean = false;          // true while Twilio is recovering a dropped media/signaling link
  callQualityWarning: string | null = null; // active call-quality warning name (e.g. high-packet-loss)
  constructor(private _local: localStorageService, private _api: ApiService, private _shared: SharedService, public twilio: TwilioService, private ngZone: NgZone) { }
  private device: Device | null | undefined;
  showIcomming = false;
  /**
   * Set while the call is being drawn by a host that owns its own surface —
   * contract details renders it inside the communication drawer. The shared
   * full-screen overlay in the layout stands down for as long as it is on.
   */
  renderInline = false;
  /**
   * Set when a host drawing the call itself goes away — leaving contract
   * details takes the communication drawer with it. The shared overlay picks
   * the call back up collapsed instead of thrown full screen over the page the
   * agent just moved to; they maximize it from there. One-shot: the overlay
   * clears it as it reads it, so a fresh call still opens the usual way.
   */
  resumeCollapsed = false;
  selectedNumber: any;
  selectedRecipientNumber: any;
  role_id = this._local.customGetItem('admin_profile') ? (this._local.customGetItem('admin_profile').role_id) : 0;
  currentStatus: typeof Status = Status;
  openSummaryModal: boolean = false;
  verificationStatus = null;
  e_consent: any = false;
  debt_type: any = null;
  // Account details shown in the call script (step 3)
  practice_name: any = null;
  treatment_amount: any = null;
  display_logo: any = null;
  debt_duration: any = null;
  relation: any = null;
  callSummarySaved$ = new Subject<any>();

  notifyCallSummarySaved(splitReqId: any) {
    this.callSummarySaved$.next(splitReqId);
  }

  // A call belongs to either a debt account or a business, never both. Setting one id
  // always clears the other so the previous call's context cannot leak into this one.
  setCallContext({ split_req_id = null, doctor_id = null }: { split_req_id?: any, doctor_id?: any }) {
    this.split_req_id = split_req_id;
    this.doctor_id = doctor_id;
    // Callers set subName after this if the call is to a named person.
    this.subName = '';
  }
  startDialing(){
    // Clear any state left over from a previous call so the dial modal mounts fresh
    // and the summary modal from the last call does not swallow this one.
    this.openSummaryModal = false;
    this.callDisconnected = false;
    this.isCallConnected = false;
    this.isReconnecting = false;
    this.callQualityWarning = null;
    this.callDuration = '';
    this.call_sid = '';
    this.showIcomming = true;
    if(((this.role_id != 13 || this.role_id != 1 || this.role_id != 4) && this.twilio.device)){
      this.onCallClick();
    }
  }
  //   async initTwilio(): Promise<void> {
  //   let admin_mobile =
  //     this._local.customGetItem('admin_profile')['admin_mobile'] ? this._local.customGetItem('admin_profile')['admin_mobile'] : ''; //
  //   if (admin_mobile) {
  //     this._api
  //       .api(
  //         { phone_number: '+1' + admin_mobile },
  //         'finance/generateAccessToken',
  //         'get'
  //       )
  //       .subscribe((resp) => {
  //         this.device = new Device(resp.token, {
  //           enableRingingState: true,
  //           debug: true,
  //         } as any);
  //         this.device.on('registered', () => {
  //           console.log('✅ Device ready');
  //           this.onCallClick();
  //         });
  //         this.device.on('error', (err: any) =>
  //           console.error('❌ Twilio Error:', err)
  //         );
  //         this.device.register();
  //       });
  //   }
  // }

  async onCallClick() {
    try {
      this.isInCall = true;
      if(this.role_id ==1 || this.role_id == 4|| this.role_id == 13 ){
        this.isInCall = false;
        this.showIcomming = false;
        this._shared.customPopups('You are not authorized to make calls', 1);
        return;
      }
      const connection = await this.twilio.device?.connect({
        params: { To: '+1' + this.selectedRecipientNumber },
      });

      if (!connection) {
        console.error('❌ Failed to create call connection');
        return;
      }
      this.activeCall = connection;
      this.callStartTime = Date.now();

      // ✅ When call is accepted
      connection.on('ringing', () => {
        console.log('🔔 Callee’s phone is ringing...');
      });
      connection.on('reconnecting', (err: any) => {
        this.ngZone.run(() => {
          this.isReconnecting = true;
          console.warn('🔄 Reconnecting call:', err?.code, err?.message);
        });
      });
      connection.on('reconnected', () => {
        this.ngZone.run(() => {
          this.isReconnecting = false;
          console.log('✅ Call reconnected');
        });
      });
      connection.on('warning', (name: string) => {
        this.ngZone.run(() => {
          this.callQualityWarning = name;
          console.warn('⚠️ Call quality warning:', name);
        });
      });
      connection.on('warning-cleared', (name: string) => {
        this.ngZone.run(() => {
          if (this.callQualityWarning === name) this.callQualityWarning = null;
          console.log('✅ Call quality warning cleared:', name);
        });
      });
      connection.on('accept', () => {
        this.ngZone.run(() => {
          this.callDisconnected = false;
          this.call_sid = connection.parameters?.['CallSid'] || '';
          console.log('✅ Call answered, CallSid:', this.call_sid);
          if (this.call_sid) {
            this.getcallsid();
          }
          this.isCallConnected = true;
        });
      });

      // ✅ When call disconnects
      connection.on('disconnect', () => {
        // Twilio fires this outside Angular's zone (e.g. when the other party
        // hangs up); run it inside so the call summary modal actually renders.
        this.ngZone.run(() => {
          this.call_sid = connection.parameters?.['CallSid'] || '';
          this.isInCall = false;
          this.callDisconnected = true;
          setTimeout(() => {
            console.log('📴 Call disconnected:', this.call_sid);
            this.endCall();
          }, 1000);
        });
      });

      // ✅ When an error occurs
      connection.on('error', (err: any) => {
        this.ngZone.run(() => {
          console.error('❌ Call error:', err);
          this.endCall();
        });
      });

    } catch (error) {
      console.error('❌ Error initiating call:', error);
      this.endCall();
    }
  }
  private endCall(): void {
    if (this.activeCall){
      this.activeCall.disconnect();
      this.activeCall = null;
    }
    this.isMuted = false;
    this.callDuration = '';
    this.callDisconnected = false;
    this.isInCall = false;
    this.openSummaryModal = true;
    this.showIcomming = false;
    this.isReconnecting = false;
    this.callQualityWarning = null;
    // The next call decides its own surface.
    this.renderInline = false;
    this.resumeCollapsed = false;
    this.isCallConnected = false;
    this.activeCall = null;
    this.sentDigits = '';
  }
  toggleMute() {
  if (!this.activeCall) return;

  this.isMuted = !this.isMuted;
  this.activeCall.mute(this.isMuted);

  console.log(this.isMuted ? '🔇 Muted' : '🔊 Unmuted');
}
  getcallsid() {
    this._api
      .api(
        {
          callSid: this.call_sid,
          to_number: '+1' + this.selectedRecipientNumber,
          from_number: '+1' + this.selectedNumber,
          country_id: 1,
          ...buildCommunicationRef(this.split_req_id, this.contract_id),
          doctor_id: this.doctor_id,
        },
        'guest/twilio_voice'
      )
      .subscribe((res) => {
        console.log(res);
      });
  }
  Callended(){
    this.twilio.device?.disconnectAll();
    this.activeCall?.disconnect();
    this.callDisconnected = true;
    this.isInCall = false;
    setTimeout(() => {
        // this.Callended();
        console.log('📴 Call disconnected:', this.call_sid);
        this.endCall();
      }, 1000);
    // this._shared.customPopups('call ended successfully', 0);
  }
  onHangupClick() {
    this.twilio.device?.disconnectAll();
    this.activeCall?.disconnect();
    this.callDisconnected = true;
    this.isInCall = false;
    setTimeout(() => {
      // this.Callended();
      console.log('📴 Call disconnected:', this.call_sid);
      this.endCall();
      if (this.call_sid){
        const data = {
          CallSid: this.call_sid,
        };
        this._api.api(data, 'guest/update_twilio_call_status').subscribe((resp) => {
          if (resp && !resp.is_error) {
            this.callDuration = null;
            this.isCallConnected = false;
            // this._shared.customPopups('call ended successfully', 0);
            // this.showIcomming = false;
          } else {
            // this._shared.customPopups('call ended successfully', 0);
          }
        });
      }
    }, 1000);
  }

    trackDuration() {
    const startTime = Date.now();
    let total_duration = '';
  
    const interval = setInterval(() => {
      if (this.callingStatus !== this.currentStatus.InProgress) {
        this.total_duration = this.callDuration;
        clearInterval(interval);
        return;
      }
  
      const elapsedMs = Date.now() - startTime;
      const totalSeconds = Math.floor(elapsedMs / 1000);
  
      const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, '0');
      const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  
      this.callDuration = `${minutes}:${seconds}`;
    }, 1000);
    total_duration = this.callDuration;
    console.log(total_duration);
  }

  /** Digits already sent on the current call, kept for on-screen feedback only. */
  sentDigits: string = '';

  /** DTMF only reaches the receiver once their side has actually answered. */
  canSendDigits(): boolean {
    return !!this.activeCall && this.isCallConnected && !this.callDisconnected;
  }

  /**
   * Plays a DTMF tone into the live call so the admin can drive the receiver's
   * IVR ("press 2 for billing") from the keypad in the call modal.
   */
  sendDigit(digit: string): boolean {
    if (!/^[0-9*#]$/.test(digit)) {
      return false;
    }
    if (!this.canSendDigits()) {
      console.warn('⌨️ Digit ignored, call is not connected yet:', digit);
      return false;
    }
    try {
      this.activeCall.sendDigits(digit);
      this.sentDigits = (this.sentDigits + digit).slice(-24);
      console.log('⌨️ DTMF sent:', digit);
      return true;
    } catch (err) {
      console.error('❌ Failed to send DTMF digit:', digit, err);
      return false;
    }
  }

  /** Trims the on-screen readout only — a tone that was played cannot be taken back. */
  backspaceDigit(): void {
    this.sentDigits = this.sentDigits.slice(0, -1);
  }

}
